import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { RefreshCw, TrendingUp, Zap, Activity, BarChart3, MapPin, X, Download, FileText } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import api from '../../lib/api';
import { toast } from 'sonner';

// Color palette for charts
const COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#06b6d4', '#84cc16'];

const SessionAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [period, setPeriod] = useState('7');
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({ chargers: [], sites: [] });
  const [selectedCharger, setSelectedCharger] = useState('all');
  const [selectedSite, setSelectedSite] = useState('all');

  // Fetch filter options (chargers & sites)
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/sessions/filter-options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = { days: period };
      if (selectedCharger !== 'all') params.charger_id = selectedCharger;
      if (selectedSite !== 'all') params.site_id = selectedSite;
      
      const response = await api.get('/sessions/analytics', { params });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Gabim në ngarkimin e analitikës');
    } finally {
      setLoading(false);
    }
  };

  // Export analytics to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = { format: 'csv', days: period };
      if (selectedCharger !== 'all') params.charger_id = selectedCharger;
      if (selectedSite !== 'all') params.site_id = selectedSite;
      
      const response = await api.get('/sessions/analytics/export', {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AlbMove_Analitika_${period}dite.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Eksporti CSV u krye me sukses');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Gabim gjatë eksportimit');
    } finally {
      setExporting(false);
    }
  };

  // Export analytics to PDF (generates HTML and prints)
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = { format: 'json', days: period };
      if (selectedCharger !== 'all') params.charger_id = selectedCharger;
      if (selectedSite !== 'all') params.site_id = selectedSite;
      
      const response = await api.get('/sessions/analytics/export', { params });
      const data = response.data;
      
      // Create printable HTML
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>AlbMove - Raport Analitik</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
            h1 { color: #8b5cf6; border-bottom: 2px solid #8b5cf6; padding-bottom: 10px; }
            h2 { color: #555; margin-top: 30px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background: #f5f5f5; }
            .summary { background: #f8f8f8; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .summary-item { margin: 10px 0; }
            .summary-value { font-size: 24px; font-weight: bold; color: #8b5cf6; }
            .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>AlbMove - Raport Analitik</h1>
          <p>Periudha: ${period} ditë</p>
          
          <div class="summary">
            <h2>Përmbledhje</h2>
            <div class="summary-item">
              <span class="summary-value">${data.summary?.total_sessions || 0}</span> Sesione Totale
            </div>
            <div class="summary-item">
              <span class="summary-value">${data.summary?.total_energy?.toFixed(2) || 0}</span> kWh Energji
            </div>
            <div class="summary-item">
              <span class="summary-value">${data.summary?.total_revenue?.toLocaleString() || 0}</span> Lekë Të Ardhura
            </div>
          </div>
          
          <h2>Të Dhënat Ditore</h2>
          <table>
            <tr><th>Data</th><th>Sesione</th><th>Energji (kWh)</th><th>Të Ardhura (Lekë)</th></tr>
            ${data.daily_data?.map(d => `<tr><td>${d.date}</td><td>${d.sessions}</td><td>${d.energy}</td><td>${d.revenue}</td></tr>`).join('') || ''}
          </table>
          
          <h2>Shpërndarja sipas Statusit</h2>
          <table>
            <tr><th>Statusi</th><th>Numri</th></tr>
            ${data.status_data?.map(s => `<tr><td>${s.status || 'N/A'}</td><td>${s.count}</td></tr>`).join('') || ''}
          </table>
          
          <h2>Top 10 Karikuesit</h2>
          <table>
            <tr><th>Karikuesi</th><th>Sesione</th><th>Energji (kWh)</th></tr>
            ${data.chargers_data?.map(c => `<tr><td>${c.charger}</td><td>${c.sessions}</td><td>${c.energy}</td></tr>`).join('') || ''}
          </table>
          
          <div class="footer">
            <p>Gjeneruar nga AlbMove CSMS - ${new Date().toLocaleDateString('sq-AL')} ${new Date().toLocaleTimeString('sq-AL')}</p>
          </div>
        </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
      
      toast.success('PDF u gjenerua - printoje ose ruaje si PDF');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Gabim gjatë gjenerimit të PDF');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [period, selectedCharger, selectedSite]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedCharger('all');
    setSelectedSite('all');
  };

  // Check if any filter is active
  const hasActiveFilters = selectedCharger !== 'all' || selectedSite !== 'all';

  // Format date for display (DD.MM)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}.${parts[1]}`;
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-zinc-800 border border-white/10 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium mb-1">{formatDate(label)}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
              {entry.dataKey === 'energy' ? ' kWh' : entry.dataKey === 'revenue' ? ' Lekë' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading && !analytics) {
    return (
      <Card className="bg-zinc-900/50 border-white/10">
        <CardContent className="p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-500 mx-auto" />
          <p className="text-zinc-400 mt-2">Duke ngarkuar analitikën...</p>
        </CardContent>
      </Card>
    );
  }

  if (!analytics) return null;

  return (
    <div className="space-y-6" data-testid="session-analytics">
      {/* Header with Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white font-outfit">Analitika e Sesioneve</h2>
              <p className="text-zinc-400 text-sm">Statistika dhe grafikë</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={exporting} data-testid="export-analytics-btn">
                  {exporting ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Eksporto
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-800 border-white/10">
                <DropdownMenuItem 
                  onClick={handleExportCSV}
                  className="text-zinc-300 hover:!bg-white/10 cursor-pointer"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Shkarko CSV
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleExportPDF}
                  className="text-zinc-300 hover:!bg-white/10 cursor-pointer"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Gjeneро PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button variant="ghost" onClick={fetchAnalytics} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Period Selector */}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px] bg-zinc-800 border-white/10" data-testid="period-select">
              <SelectValue placeholder="Periudha" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10">
              <SelectItem value="7">7 ditë</SelectItem>
              <SelectItem value="14">14 ditë</SelectItem>
              <SelectItem value="30">30 ditë</SelectItem>
              <SelectItem value="60">60 ditë</SelectItem>
            </SelectContent>
          </Select>

          {/* Charger Filter */}
          <Select value={selectedCharger} onValueChange={setSelectedCharger}>
            <SelectTrigger className="w-[200px] bg-zinc-800 border-white/10" data-testid="charger-filter">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-500" />
                <SelectValue placeholder="Karikuesi" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10 max-h-[300px]">
              <SelectItem value="all">Të gjithë karikuesit</SelectItem>
              {filterOptions.chargers.map((charger) => (
                <SelectItem key={charger.id} value={charger.id}>
                  <div className="flex items-center justify-between w-full">
                    <span>{charger.name}</span>
                    <span className="text-xs text-zinc-500 ml-2">({charger.session_count})</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Site Filter */}
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-[200px] bg-zinc-800 border-white/10" data-testid="site-filter">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" />
                <SelectValue placeholder="Lokacioni" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10 max-h-[300px]">
              <SelectItem value="all">Të gjitha lokacionet</SelectItem>
              {filterOptions.sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>
                  <div className="flex flex-col">
                    <span>{site.name}</span>
                    <span className="text-xs text-zinc-500">{site.city} ({site.session_count})</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              onClick={clearFilters}
              className="text-zinc-400 hover:text-white"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Pastro filtrat
            </Button>
          )}
        </div>

        {/* Active Filter Badge */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-zinc-500">Duke filtruar:</span>
            {selectedCharger !== 'all' && (
              <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">
                Karikuesi: {filterOptions.chargers.find(c => c.id === selectedCharger)?.name || selectedCharger}
              </span>
            )}
            {selectedSite !== 'all' && (
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                Lokacioni: {filterOptions.sites.find(s => s.id === selectedSite)?.name || selectedSite}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sessions per Day - Line Chart */}
        <Card className="bg-zinc-900/50 border-white/10 col-span-1 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              Sesionet dhe Energjia për Ditë
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics.daily_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    stroke="#71717a"
                    tick={{ fill: '#a1a1aa', fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="left"
                    stroke="#71717a"
                    tick={{ fill: '#a1a1aa', fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right"
                    stroke="#71717a"
                    tick={{ fill: '#a1a1aa', fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    formatter={(value) => <span className="text-zinc-400">{value}</span>}
                  />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="sessions" 
                    name="Sesione"
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ fill: '#8b5cf6', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="energy" 
                    name="Energji (kWh)"
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={{ fill: '#10b981', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution - Pie Chart */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Activity className="w-5 h-5 text-emerald-500" />
              Shpërndarja sipas Statusit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics.status_distribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#71717a' }}
                  >
                    {analytics.status_distribution.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]}
                        stroke="transparent"
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                    formatter={(value, name) => [`${value} sesione`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Chargers - Bar Chart */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Zap className="w-5 h-5 text-yellow-500" />
              Karikuesit me më shumë Sesione
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={analytics.top_chargers} 
                  layout="vertical"
                  margin={{ left: 20, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                  <XAxis type="number" stroke="#71717a" tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={120}
                    stroke="#71717a"
                    tick={{ fill: '#a1a1aa', fontSize: 11 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value, name) => {
                      if (name === 'sessions') return [`${value} sesione`, 'Sesione'];
                      if (name === 'energy') return [`${value} kWh`, 'Energji'];
                      return [value, name];
                    }}
                  />
                  <Bar dataKey="sessions" name="sessions" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Connectors (Ports) - Bar Chart */}
        {analytics.top_connectors && analytics.top_connectors.length > 0 && (
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Activity className="w-5 h-5 text-cyan-500" />
                Portat më të Përdorura
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={analytics.top_connectors} 
                    layout="vertical"
                    margin={{ left: 20, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                    <XAxis type="number" stroke="#71717a" tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={140}
                      stroke="#71717a"
                      tick={{ fill: '#a1a1aa', fontSize: 10 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181b', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value, name) => {
                        if (name === 'sessions') return [`${value} sesione`, 'Sesione'];
                        if (name === 'energy') return [`${value} kWh`, 'Energji'];
                        return [value, name];
                      }}
                    />
                    <Bar dataKey="sessions" name="sessions" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SessionAnalytics;
